import { resolveType } from "./ServiceContainer";
import { LeagueResultService } from "./LeagueResultService";
import { LeagueService } from "./LeagueService";
import { LeagueMemberResultModel, LeagueResultModel } from "@/models/LeagueResultModels";
import { Timestamp } from "firebase/firestore";
import { CourseService } from "./CourseService";
import { LeagueFlightModel, LeagueModel, LeagueScheduleModel } from "@/models/LeagueModels";
import { CourseModel } from "@/models/CourseModels";
import { nullIf } from "@/utils/toolkit";
import { UserDashboardService } from "./UserDashboardService";
import { LeagueMembershipService } from "./LeagueMembershipService";

// src/services/LeagueMembershipService.ts
export class LeagueAdminService {
    constructor() { }

    private async loadLeague(leagueId: string): Promise<LeagueModel> {
        const leagueService = resolveType(LeagueService);
        const leagueModel = await leagueService.get(leagueId);
        if (!leagueModel) {
            throw new Error(`League not found: ${leagueId}`);
        }
        return leagueModel;
    }

    private async loadResults(leagueId: string, dayId: string, errorIfNotFound: boolean = true): Promise<LeagueResultModel | null> {
        const resultId = `${leagueId}_${dayId}`;
        const leagueResultService = resolveType(LeagueResultService);
        const leagueResultModel = await leagueResultService.get(resultId);
        if (!leagueResultModel) {
            if (errorIfNotFound) {
                throw new Error(`League result not found: ${resultId}`);
            } else {
                return null;
            }
        }
        leagueResultModel.id = leagueResultModel.id || resultId;
        leagueResultModel.leagueId = leagueResultModel.leagueId || leagueId;
        leagueResultModel.dayId = leagueResultModel.dayId || dayId;
        leagueResultModel.members = leagueResultModel.members || {};
        leagueResultModel.pinPrizes = leagueResultModel.pinPrizes || [];
        return leagueResultModel;
    }

    private findSchedule(leagueModel: LeagueModel, dayId: string): LeagueScheduleModel {
        const scheduleModel = leagueModel.schedule.find(s => s.dayId == dayId);
        if (!scheduleModel) {
            throw new Error(`Schedule not found: ${dayId}`);
        }
        return scheduleModel;
    }

    private async loadCourse(courseId: string): Promise<CourseModel> {
        const courseService = resolveType(CourseService);
        const courseModel = await courseService.get(courseId);
        if (!courseModel) {
            throw new Error(`Course not found: ${courseId}`);
        }
        return courseModel;
    }

    private initLeagueMemberResultData(leagueModel: LeagueModel, leagueResultModel: LeagueResultModel): void {
        // now we need to update the leagueResultModel with the members from the leagueModel
        // if there's already a member in the leagueResultModel, we need to use the data it has and fill in any missing data from the leagueModel
        Object.entries(leagueModel.members).forEach(([encodedEmail, member]) => {
            const existingResultMember = leagueResultModel.members[encodedEmail];
            leagueResultModel.members[encodedEmail] = {
                uid: member.uid,
                encodedEmail: encodedEmail,
                holeStats: existingResultMember?.holeStats || [],
                holesCompleted: (existingResultMember?.holeStats || []).filter(h => (h.grossScore || 0) > 0).length || existingResultMember?.holesCompleted || 0,
                teeName: (leagueModel.flights || []).find(f => f.id == member.flightId)?.teeName || '',
                flightId: member?.flightId || '',
                prevFlightId: existingResultMember?.flightId || null,
                prevHandicap: existingResultMember?.handicap || null,
                handicap: member?.handicap || -10,  // note the handicap from leagueModel takes precedence over the leagueResultModel 
                handicapCalcSteps: [],
                grossScore: existingResultMember?.grossScore || 0,
                grossScoreAdjusted: 0,
                grossScoreRanked: 0,
                grossRank: 0,
                grossRankText: '',
                netScore: 0,
                netScoreRanked: 0,
                netRank: 0,
                netRankText: '',
                payouts: [],
                roundId: '',
                totalLeaguePayout: 0,
                totalGrossSkinsPayout: 0,
                totalNetSkinsPayout: 0,
                totalPayout: 0,
            };

        });
    }



    private calculateStrokePayouts(members: LeagueMemberResultModel[], flight: LeagueFlightModel): void {
        // first, let's make sure the birdies are set properly
        members.forEach((member, _) => {

            // now, let's calculate the stroke-based payouts
            member.holeStats.forEach((hole, _) => {
                if (hole.grossScore == 1) {
                    member.payouts.push({
                        id: crypto.randomUUID(),
                        hole: hole.hole,
                        teeName: flight.teeName,
                        code: 'GRSACE',
                        amount: null,
                        description: 'Ace',
                    });
                } else if (hole.grossScore == hole.par - 3) {
                    member.payouts.push({
                        id: crypto.randomUUID(),
                        hole: hole.hole,
                        teeName: flight.teeName,
                        code: 'GRSALB',
                        amount: null,
                        description: 'Albatross',
                    });
                } else if (hole.grossScore == hole.par - 2) {
                    member.payouts.push({
                        id: crypto.randomUUID(),
                        hole: hole.hole,
                        teeName: flight.teeName,
                        code: 'GRSEGL',
                        amount: 5,
                        description: 'Eagle',
                    });
                } else if (hole.grossScore == hole.par - 1) {
                    member.payouts.push({
                        id: crypto.randomUUID(),
                        hole: hole.hole,
                        teeName: flight.teeName,
                        code: 'GRSBRD',
                        amount: 2,
                        description: 'Birdie',
                    });
                } else if (hole.netScore == hole.par - 1) {
                    // low does not pay out net birdies.
                    if (flight.id.includes('_mid')) {
                        // mid pays out only a single net birdie.
                        if (member.payouts.filter(p => p.code === 'NETBRD').length === 0) {
                            member.payouts.push({
                                id: crypto.randomUUID(),
                                hole: hole.hole,
                                teeName: flight.teeName,
                                code: 'NETBRD',
                                amount: 2,
                                description: 'Net Birdie',
                            });
                        }
                    } else if (flight.id.includes('_high')) {
                        // high pays out up to 2 net birdies.
                        if (member.payouts.filter(p => p.code === 'NETBRD').length <= 1) {
                            member.payouts.push({
                                id: crypto.randomUUID(),
                                hole: hole.hole,
                                teeName: flight.teeName,
                                code: 'NETBRD',
                                amount: 2,
                                description: 'Net Birdie',
                            });
                        }
                    }
                }
            });
        });
    }

    private calculateRanks(members: LeagueMemberResultModel[]): void {

        let passes = 0;
        //console.log('flight', flight.id);
        // const payoutAmounts = {
        //     '1': 12,
        //     '2': 8,
        //     '3': 6,
        //     'T1': 20,
        //     'T2': 14,
        //     'T3': 6
        // };

        while (passes < 50) {
            // continue to rank until no one person is in top 3 for both gross and net.

            // first, remove all payouts that are related to either gross or net ranking.
            // this is so that we can make multiple passes if needed in case one member wins both gross and net.
            // a subsequent pass (if needed) will bump the grossScoreRanked or netScoreRanked by 1000 to make them ineligible for that payout.
            members.forEach(m => {
                m.payouts = m.payouts.filter(p => !p.code?.includes('GRSRNK') && !p.code?.includes('NETRNK'));
            });

            // now sort by grossScoreRanked
            const eligibleMembers = members.filter(m => m.holesCompleted == 9);
            eligibleMembers.sort((a, b) => a.grossScoreRanked - b.grossScoreRanked);

            // Handle gross ranks with proper tie handling
            if (eligibleMembers.length > 0) {
                let currentGrossRank = 1;
                let previousGrossScore = eligibleMembers[0]?.grossScoreRanked;
                let membersAtPreviousGrossScore = eligibleMembers.filter(m => m.grossScoreRanked === previousGrossScore).length;

                eligibleMembers.forEach((member, _) => {
                    const membersAtCurrentGrossScore = eligibleMembers.filter(m => m.grossScoreRanked === member.grossScoreRanked).length;
                    if (member.grossScoreRanked !== previousGrossScore) {
                        currentGrossRank += membersAtPreviousGrossScore;
                        previousGrossScore = member.grossScoreRanked;
                        membersAtPreviousGrossScore = membersAtCurrentGrossScore;
                    }

                    member.grossRank = currentGrossRank;
                    member.grossRankText = membersAtCurrentGrossScore > 1 ? `T${currentGrossRank}` : `${currentGrossRank}`;
                });

                // eligibleMembers.forEach(m => {
                //     console.log('member', m.encodedEmail, m.grossRank, m.grossRankText);
                // });

                // determine payout for 1st gross rank
                // 1st place: 12
                // 2nd place: 8
                // 3rd place: 6
                //
                // 2-way tie for 1st place: 12 + 8 = 20 total to split
                // There will never be a 2nd place in this case.
                // 3rd place: 6

                // 3+-way tie for 1st place: 12 + 8 + 6 = 26 total to split
                // There will never be a 2nd place in this case.
                // There will never be a 3rd place in this case.

                // 1st place: 12
                // 2+-way tie for 2nd place: 8 + 6 = 14 total to split
                // There will never be a 3rd place in this case.

                const grossRank1members = eligibleMembers.filter(m => m.grossRank === 1);
                if (grossRank1members.length == 1) {
                    grossRank1members[0].payouts.push({
                        id: crypto.randomUUID(),
                        hole: null,
                        teeName: null,
                        code: 'GRSRNK1ST',
                        amount: 12,
                        description: 'Gross 1st Place Outright',
                    });
                } else if (grossRank1members.length > 1) {
                    const totalAmount = 12 + (grossRank1members.length === 2 ? 8 : 0) + (grossRank1members.length >= 3 ? 6 : 0);
                    let amountPerMember = Math.round(totalAmount / grossRank1members.length);
                    if (amountPerMember < 1) {
                        amountPerMember = 1;
                    }
                    grossRank1members.forEach(m => {
                        m.payouts.push({
                            id: crypto.randomUUID(),
                            hole: null,
                            teeName: null,
                            code: 'GRSRNK1ST',
                            amount: amountPerMember,
                            description: `Gross 1st Place: ${grossRank1members.length}-way tie`,
                        });
                    });
                }

                // determine payout for 2nd gross rank
                // if there's a tie for 1st place, we won't have a 2nd place.
                const grossRank2members = eligibleMembers.filter(m => m.grossRank === 2);
                if (grossRank2members.length == 1) {
                    grossRank2members[0].payouts.push({
                        id: crypto.randomUUID(),
                        hole: null,
                        teeName: null,
                        code: 'GRSRNK2ND',
                        amount: 8,
                        description: 'Gross 2nd Place Outright',
                    });
                } else if (grossRank2members.length > 1) {
                    const totalAmount = 8 + (grossRank2members.length >= 2 ? 6 : 0);
                    let amountPerMember = Math.round(totalAmount / grossRank2members.length);
                    if (amountPerMember < 1) {
                        amountPerMember = 1;
                    }
                    grossRank2members.forEach(m => {
                        m.payouts.push({
                            id: crypto.randomUUID(),
                            hole: null,
                            teeName: null,
                            code: 'GRSRNK2ND',
                            amount: amountPerMember,
                            description: `Gross 2nd Place: ${grossRank2members.length}-way tie`,
                        });
                    });
                }

                // determine payout for 3rd gross rank
                // if there's a 2-way tie for 1st place, we won't have a 2nd place but we will have a 3rd place.
                // if there's a 3+-way tie for 1st place, or 2+-way tie for 2nd place, we won't have a 3rd place.
                const grossRank3members = eligibleMembers.filter(m => m.grossRank === 3);
                if (grossRank3members.length == 1) {
                    grossRank3members[0].payouts.push({
                        id: crypto.randomUUID(),
                        hole: null,
                        teeName: null,
                        code: 'GRSRNK3RD',
                        amount: 6,
                        description: 'Gross 3rd Place Outright',
                    });
                } else if (grossRank3members.length > 1) {
                    const totalAmount = 6;
                    let amountPerMember = Math.round(totalAmount / grossRank3members.length);
                    if (amountPerMember < 1) {
                        amountPerMember = 1;
                    }
                    grossRank3members.forEach(m => {
                        m.payouts.push({
                            id: crypto.randomUUID(),
                            hole: null,
                            teeName: null,
                            code: 'GRSRNK3RD',
                            amount: amountPerMember,
                            description: `Gross 3rd Place: ${grossRank3members.length}-way tie`,
                        });
                    });
                }
            }


            // now sort by netScoreRanked
            eligibleMembers.sort((a, b) => a.netScoreRanked - b.netScoreRanked);


            let currentNetRank = 1;
            let previousNetScore = eligibleMembers[0]?.netScoreRanked;
            let membersAtPreviousNetScore = eligibleMembers.filter(m => m.netScoreRanked === previousNetScore).length;

            eligibleMembers.forEach((member, _) => {
                const membersAtCurrentNetScore = eligibleMembers.filter(m => m.netScoreRanked === member.netScoreRanked).length;
                if (member.netScoreRanked !== previousNetScore) {
                    currentNetRank += membersAtPreviousNetScore;
                    previousNetScore = member.netScoreRanked;
                    membersAtPreviousNetScore = membersAtCurrentNetScore;
                }

                member.netRank = currentNetRank;
                member.netRankText = membersAtCurrentNetScore > 1 ? `T${currentNetRank}` : `${currentNetRank}`;
            });



            // determine payout for 1st net rank
            // 1st place: 12
            // 2nd place: 8
            // 3rd place: 6
            //
            // 2-way tie for 1st place: 12 + 8 = 20 total to split
            // There will never be a 2nd place in this case.
            // 3rd place: 6

            // 3+-way tie for 1st place: 12 + 8 + 6 = 26 total to split
            // There will never be a 2nd place in this case.
            // There will never be a 3rd place in this case.

            // 1st place: 12
            // 2+-way tie for 2nd place: 8 + 6 = 14 total to split
            // There will never be a 3rd place in this case.

            const netRank1members = eligibleMembers.filter(m => m.netRank === 1);
            if (netRank1members.length == 1) {
                netRank1members[0].payouts.push({
                    id: crypto.randomUUID(),
                    hole: null,
                    teeName: null,
                    code: 'NETRNK1ST',
                    amount: 12,
                    description: 'Net 1st Place Outright',
                });
            } else if (netRank1members.length > 1) {
                const totalAmount = 12 + (netRank1members.length === 2 ? 8 : 0) + (netRank1members.length >= 3 ? 6 : 0);
                let amountPerMember = Math.round(totalAmount / netRank1members.length);
                if (amountPerMember < 1) {
                    amountPerMember = 1;
                }
                netRank1members.forEach(m => {
                    m.payouts.push({
                        id: crypto.randomUUID(),
                        hole: null,
                        teeName: null,
                        code: 'NETRNK1ST',
                        amount: amountPerMember,
                        description: `Net 1st Place: ${netRank1members.length}-way tie`,
                    });
                });
            }

            // determine payout for 2nd net rank
            // if there's a tie for 1st place, we won't have a 2nd place.
            const netRank2members = eligibleMembers.filter(m => m.netRank === 2);
            if (netRank2members.length == 1) {
                netRank2members[0].payouts.push({
                    id: crypto.randomUUID(),
                    hole: null,
                    teeName: null,
                    code: 'NETRNK2ND',
                    amount: 8,
                    description: 'Net 2nd Place Outright',
                });
            } else if (netRank2members.length > 1) {
                const totalAmount = 8 + (netRank2members.length >= 2 ? 6 : 0);
                let amountPerMember = Math.round(totalAmount / netRank2members.length);
                if (amountPerMember < 1) {
                    amountPerMember = 1;
                }
                netRank2members.forEach(m => {
                    m.payouts.push({
                        id: crypto.randomUUID(),
                        hole: null,
                        teeName: null,
                        code: 'NETRNK2ND',
                        amount: amountPerMember,
                        description: `Net 2nd Place: ${netRank2members.length}-way tie`,
                    });
                });
            }

            // determine payout for 3rd net rank
            // if there's a 2-way tie for 1st place, we won't have a 2nd place but we will have a 3rd place.
            // if there's a 3+-way tie for 1st place, or 2+-way tie for 2nd place, we won't have a 3rd place.
            const netRank3members = eligibleMembers.filter(m => m.netRank === 3);
            if (netRank3members.length == 1) {
                netRank3members[0].payouts.push({
                    id: crypto.randomUUID(),
                    hole: null,
                    teeName: null,
                    code: 'NETRNK3RD',
                    amount: 6,
                    description: 'Net 3rd Place Outright',
                });
            } else if (netRank3members.length > 1) {
                const totalAmount = 6;
                let amountPerMember = Math.round(totalAmount / netRank3members.length);
                if (amountPerMember < 1) {
                    amountPerMember = 1;
                }
                netRank3members.forEach(m => {
                    m.payouts.push({
                        id: crypto.randomUUID(),
                        hole: null,
                        teeName: null,
                        code: 'NETRNK3RD',
                        amount: amountPerMember,
                        description: `Net 3rd Place: ${netRank3members.length}-way tie`,
                    });
                });
            }



            // now, check if there are any members in top 3 for both gross and net.
            const allEligibleMembers = members.filter(m => m.holesCompleted == 9);
            const membersInTop3GrossAndNet = allEligibleMembers.filter(m => (m.grossRank || 0) > 0 && (m.grossRank || 0) <= 3 && (m.netRank || 0) > 0 && (m.netRank || 0) <= 3);
            if (membersInTop3GrossAndNet.length == 0) {
                // this causes the loop to end
                passes = 1000;
            } else {
                // determine which payout (gross or net) is higher for the first member
                const member = membersInTop3GrossAndNet[0];
                const grossRankPayout = member.payouts.find(p => p.code?.includes('GRSRNK'));
                const netRankPayout = member.payouts.find(p => p.code?.includes('NETRNK'));
                if (grossRankPayout && netRankPayout) {
                    if ((grossRankPayout.amount || 0) >= (netRankPayout.amount || 0)) {
                        // gross pays more or the same. bump net score to 1000 to make them ineligible for net payout on the next pass.
                        member.netScoreRanked += 1000;
                    } else {
                        // by definition, net pays more here. so let's bump up gross score ranked to disqualify them from gross.
                        // //if ((netRankPayout.amount || 0) > (grossRankPayout.amount || 0)) {
                        // net pays more. bump gross score to 1000 to make it ineligible on the next pass.
                        member.grossScoreRanked += 1000;
                    }
                }
            }
            passes++;
        }

        // for all those who did not record a score, let's set things appropriately.
        Object.values(members).forEach(member => {
            if (member.holesCompleted == 0) {
                member.grossScoreRanked = 2000;
                member.grossRankText = 'WD';
                member.netScoreRanked = 2000;
                member.netRankText = 'WD';
            }
        });
    }

    /** Calculate the handicaps for each member. Should only be called when activating a day, as handicap drives all net calculations.*/
    private async calculateHandicaps(leagueModel: LeagueModel, courseModel: CourseModel, resultModel: LeagueResultModel): Promise<void> {
        // pull all resultModels up to the current day (maximum of 20)
        const leagueResultService = resolveType(LeagueResultService);
        const allResults: LeagueResultModel[] = [];

        for (const schedule of leagueModel.schedule) {
            if (schedule.dayId <= resultModel.dayId) {
                const resultId = `${leagueModel.id}_${schedule.dayId}`;
                const tempResult = await leagueResultService.get(resultId);
                if (!tempResult) {
                    throw new Error(`League result not found: ${resultId}`);
                }
                allResults.push(tempResult);
            } else {
                console.log(`skipping resultModel for ${schedule.dayId} because it's in the future`);
            }
        }

        const parFor9Holes = courseModel.scorecard.holes.slice(0, 9).reduce((acc, hole) => acc + hole.par, 0);


        // now, let's calculate the handicaps
        for (const member of Object.values(resultModel.members)) {

            const leagueMember = leagueModel.members[member.encodedEmail];

            member.handicapCalcSteps = [];
            member.prevFlightId = leagueMember?.flightId || null;
            member.prevHandicap = leagueMember?.handicap || null;

            // grab all the scores for this member
            const adjustedGrossScores = allResults.map(r => r.members[member.encodedEmail].grossScoreAdjusted);
            // console.log(`adjustedGrossScores: ${adjustedGrossScores}`);

            console.log(`calculating handicap for ${member.encodedEmail} as of ${resultModel.dayId}, starting with AGS of ${adjustedGrossScores.join(', ')}`);

            // ok we have all the adjusted gross scores for this member.
            // take the most recent 5 scores.
            const mostRecentEligibleScores = adjustedGrossScores.filter(s => s !== null && s !== undefined && s !== 0).slice(-6);
            // sort by score
            mostRecentEligibleScores.sort((a, b) => a - b);

            member.handicapCalcSteps.push(`Most recent 6 AGS: ${mostRecentEligibleScores.join(', ')}`);

            if (mostRecentEligibleScores.length === 6) {
                // remove the lowest score (first one)
                mostRecentEligibleScores.shift();

            }
            if (mostRecentEligibleScores.length === 5) {
                // remove the highest score (last one)
                mostRecentEligibleScores.pop();
            }

            member.handicapCalcSteps.push(`Removing all but the 4 middlest scores: ${mostRecentEligibleScores.join(', ')}`);

            if (mostRecentEligibleScores.length === 4) {
                // ok now we have the 4 middlest scores in our array.
                // calculate the average
                const averageScore = mostRecentEligibleScores.reduce((acc, score) => acc + score, 0) / mostRecentEligibleScores.length;
                const roundedAverageScore = Math.round(averageScore);

                member.handicapCalcSteps.push(`Average score: ${averageScore}, rounding to ${roundedAverageScore}`);

                // now let's write those handicaps to the member object on the leagueModel, NOT the resultModel.
                // resultModel is a point in time; leagueModel is the current state of the league (i.e. for next week)
                const leagueMember = leagueModel.members[member.encodedEmail];
                const newHandicap = Math.round(roundedAverageScore) - parFor9Holes;
                member.handicapCalcSteps.push(`Avg score - Par = New handicap: ${roundedAverageScore} - ${parFor9Holes} = ${newHandicap}`);

                if (newHandicap !== (leagueMember.handicap || 0)) {
                    member.handicapCalcSteps.push(`Handicap changed from ${leagueMember.handicap} to ${newHandicap}`);
                    //console.log(`new handicap for ${member.encodedEmail} went from ${leagueMember.handicap} to ${newHandicap}`);
                    leagueMember.handicap = newHandicap;
                    member.handicap = newHandicap;

                    const teeName = leagueModel.flights.find(f => f.id === leagueMember.flightId)?.teeName || '';
                    if (teeName === '') {
                        throw new Error(`Could not find teeName for flightId=${leagueMember.flightId} for member=${leagueMember.encodedEmail}`);
                    }

                    const oldFlight = leagueModel.flights.find(f => f.id === leagueMember.flightId);
                    const newFlight = leagueModel.flights.find(f => f.teeName == teeName && f.handicapMin <= newHandicap && newHandicap <= f.handicapMax);
                    if (!newFlight) {
                        throw new Error(`Could not find flightId for tee=${teeName}, handicap=${newHandicap}`);
                    } else if (newFlight.id !== leagueMember.flightId) {
                        member.handicapCalcSteps.push(`Flight changed from ${oldFlight?.name || leagueMember.flightId} to ${newFlight.name} due to change in handicap`);
                        //console.log(`new flightId for ${member.encodedEmail} went from ${leagueMember.flightId} to ${newFlight.id}`);
                        leagueMember.flightId = newFlight.id;
                        member.flightId = newFlight.id;
                    } else {
                        //console.log(`no change in flightId for ${member.encodedEmail} of ${leagueMember.flightId}`);
                        member.handicapCalcSteps.push(`Flight remains at ${oldFlight?.name || leagueMember.flightId} even though handicap changed`);
                    }
                } else {
                    //console.log(`no change in handicap for ${member.encodedEmail} of ${newHandicap}`);
                    member.handicapCalcSteps.push(`Result: No change in handicap; handicap matches previous value of ${leagueMember.handicap}`);
                }
            } else {
                // we don't have enough scores to calculate a new handicap.
                //console.log(`no change in handicap for ${member.encodedEmail} of ${member.handicap} due to lack of sufficient scores (less than 3)`);
                member.handicapCalcSteps.push(`Result: No change in handicap of ${member.handicap}; not enough scores`);
            }

            console.log(`${member.encodedEmail} handicap: ${member.handicap} calcSteps: ${member.handicapCalcSteps.join(', ')}`);

        }

        // now that we have recalculated (or propagated) the handicaps and flightIds, update the leagueResultModel
        console.log(`recalculated handicaps and flightIds for leagueResultModel ${resultModel.id}, updating...`);
        await leagueResultService.update(resultModel.id, resultModel);
    }

    private async calculateSkins(leagueModel: LeagueModel, courseModel: CourseModel, resultModel: LeagueResultModel): Promise<void> {
        const scheduleModel = this.findSchedule(leagueModel, resultModel.dayId);
        if (!scheduleModel.skinsDayId1) {
            // not the end of a skins session. do nothing.
            return;
        }

        const prevScheduleModel = this.findSchedule(leagueModel, scheduleModel.skinsDayId1!);

        if (scheduleModel.status !== 'locked' && prevScheduleModel.status !== 'completed') {
            throw new Error(`Schedules must be completed to calculate skins: ${scheduleModel.skinsDayId1} or ${scheduleModel.dayId}`);
        }

        const prevResultsId = `${leagueModel.id}_${prevScheduleModel.dayId}`;
        const leagueResultService = resolveType(LeagueResultService);
        const prevResultModel = await leagueResultService.get(prevResultsId);
        if (!prevResultModel) {
            throw new Error(`League result not found: ${prevResultsId}`);
        }

        // reset skin prizes as we'll be recalculating those
        resultModel!.netSkinPrizes = [];
        resultModel!.grossSkinPrizes = [];

        // grab total number of members who opted into the gross skins competition. each member contributes $2 ($1 per week, 2 weeks per session)
        const totalGrossMembers = Object.values(leagueModel.members).filter(m => m.grossSkins).length;
        const totalGrossPayout = totalGrossMembers * 2; // $1 per member per week, 2 weeks per session

        // for each hole defined on the course, spin through all members eligible for gross skins and find the 
        // member id who has the lowest gross score.
        // only include those who have completed 9 holes and have opted into the gross skins competition.
        // (note we have to pull members from each of the 2 weeks in the skins session)
        const week1GrossMembers = Object.values(prevResultModel.members).filter(m => m.holesCompleted === 9 && leagueModel.members[m.encodedEmail].grossSkins);
        const grossMembers = week1GrossMembers.concat(Object.values(resultModel.members).filter(m => m.holesCompleted === 9 && leagueModel.members[m.encodedEmail].grossSkins));



        // grab total number of members who opted into the net skins competition. each member contributes $2 ($1 per week, 2 weeks per session)
        const totalNetMembers = Object.values(leagueModel.members).filter(m => m.netSkins).length;
        const totalNetPayout = totalNetMembers * 2; // $1 per member per week, 2 weeks per session

        // only include those who have completed 9 holes and have opted into the net skins competition.
        // (note we have to pull members from each of the 2 weeks in the skins session)
        const week1NetMembers = Object.values(prevResultModel.members).filter(m => m.holesCompleted === 9 && leagueModel.members[m.encodedEmail].netSkins);
        const netMembers = week1NetMembers.concat(Object.values(resultModel.members).filter(m => m.holesCompleted === 9 && leagueModel.members[m.encodedEmail].netSkins));



        // now, for each hole, find the lowest score, the members who hit that score, and if there's only 1 -- mark them as a winner
        Object.values(courseModel.scorecard.holes).forEach(h => {
            // find minimum scores
            var minGross = Math.min(...grossMembers.map(m => nullIf(m.holeStats.find(h1 => h1.hole === h.number)?.grossScore, 0) || 1000));
            // find all those who scored that minimum
            var minGrossMembers = grossMembers.filter(m => m.holeStats.find(h1 => h1.hole === h.number)?.grossScore === minGross) || [];
            //console.log(`hole: ${h.number}, minGross: ${minGross}, minGrossMembers: ${minGrossMembers.map(m => m.encodedEmail)}`);
            if (minGrossMembers.length === 1) {
                // exactly 1 person scored this. they're a skins winner!
                const id = crypto.randomUUID();
                resultModel.grossSkinPrizes.push({
                    amount: 0,
                    encodedEmail: minGrossMembers[0].encodedEmail,
                    hole: h.number,
                    grossScore: minGross,
                    netScore: null,
                    id: id
                });
                resultModel.members[minGrossMembers[0].encodedEmail!].payouts.push({
                    id: id,
                    hole: h.number,
                    teeName: minGrossMembers[0].teeName,
                    code: 'GRSSKN',
                    description: 'Gross Skin Winner',
                    amount: 0,
                });
            }

            var minNet = Math.min(...netMembers.map(m => nullIf(m.holeStats.find(h1 => h1.hole === h.number)?.netScore, 0) || 1000));
            var minNetMembers = netMembers.filter(m => m.holeStats.find(h1 => h1.hole === h.number)?.netScore === minNet);
            //console.log(`hole: ${h.number}, minNet: ${minNet}, minNetMembers: ${minNetMembers.map(m => m.encodedEmail)}`);
            if (minNetMembers.length === 1) {
                // exactly 1 person scored this. they're a skins winner!
                const id = crypto.randomUUID();
                resultModel.netSkinPrizes.push({
                    amount: 0,
                    encodedEmail: minNetMembers[0].encodedEmail,
                    hole: h.number,
                    grossScore: minNetMembers[0].holeStats.find(h1 => h1.hole === h.number)?.grossScore || null,
                    netScore: minNet,
                    id: id
                });
                resultModel.members[minNetMembers[0].encodedEmail!].payouts.push({
                    id: id,
                    hole: h.number,
                    teeName: minNetMembers[0].teeName,
                    code: 'NETSKN',
                    description: 'Net Skin Winner',
                    amount: 0,
                });
            }
        });

        // now that we have all the skin holes and members calculated, we can determine the individual payouts
        const grossPayoutPerSkin = (totalGrossPayout / resultModel.grossSkinPrizes.length);
        resultModel.grossSkinPrizes.forEach(skin => {
            skin.amount = grossPayoutPerSkin;

            resultModel.members[skin.encodedEmail!].payouts.filter(p => p.code === 'GRSSKN').forEach(p => {
                p.amount = skin.amount;
            });


            // since a single person may win multiple skins, we need to sum them all up to put their total on the members collection entry.
            resultModel.members[skin.encodedEmail!].totalGrossSkinsPayout = resultModel.grossSkinPrizes.reduce((acc, p) => acc + (skin.encodedEmail === p.encodedEmail ? p.amount || 0 : 0), 0);
        });

        const netPayoutPerSkin = (totalNetPayout / resultModel.netSkinPrizes.length);
        resultModel.netSkinPrizes.forEach(skin => {
            skin.amount = netPayoutPerSkin;
            resultModel.members[skin.encodedEmail!].payouts.filter(p => p.code === 'NETSKN').forEach(p => {
                p.amount = skin.amount;
            });

            // since a single person may win multiple skins, we need to sum them all up to put their total on the members collection entry.
            resultModel.members[skin.encodedEmail!].totalNetSkinsPayout = resultModel.netSkinPrizes.reduce((acc, p) => acc + (skin.encodedEmail === p.encodedEmail ? p.amount || 0 : 0), 0);
        });

        // we get here, we're good! only need to update the last week's results (i.e. NOT prevResultModel)
        await leagueResultService.update(resultModel.id, resultModel);

    }

    /** Creates a new leagueResultModel document for a given dayId, copying data as needed from leagueModel */
    private async createDay(leagueId: string, dayId: string): Promise<LeagueResultModel | null> {

        const leagueModel = await this.loadLeague(leagueId);
        const scheduleModel = this.findSchedule(leagueModel!, dayId);
        if (scheduleModel.status != 'notstarted' && scheduleModel.status != 'active') {
            throw new Error(`Schedule must be notstarted or active to create a result for it: ${dayId}`);
        }

        // // get the course for this league
        // const courseModel = await this.loadCourse(scheduleModel.courseId || leagueModel.courseId);

        // get the result for this day and league, if any.
        const leagueResultService = resolveType(LeagueResultService);
        const resultId = `${leagueId}_${dayId}`;
        let leagueResultModel = await this.loadResults(leagueId, dayId, false);
        if (!leagueResultModel) {
            leagueResultModel = {
                id: resultId,
                leagueId: leagueId,
                dayId: dayId,
                members: {},
                pinPrizes: [],
                grossSkinPrizes: [],
                netSkinPrizes: []
            };
            await leagueResultService.create(leagueResultModel);
        }

        this.initLeagueMemberResultData(leagueModel, leagueResultModel);

        // we get here, every member in the leagueModel should have a corresponding member in the leagueResultModel
        await leagueResultService.update(resultId, leagueResultModel);

        return leagueResultModel;
    }

    async calculateDay(leagueId: string, dayId: string): Promise<void> {

        //console.log('calculateDay', leagueId, dayId);

        const leagueModel = await this.loadLeague(leagueId);
        const leagueResultModel = await this.loadResults(leagueId, dayId);
        const scheduleModel = this.findSchedule(leagueModel, dayId);
        const courseModel = await this.loadCourse(scheduleModel.courseId || leagueModel.courseId);

        // console.log('leagueModel', leagueModel);
        // console.log('leagueResultModel', leagueResultModel);
        // console.log('scheduleModel', scheduleModel);

        if (scheduleModel.status != 'locked') {
            throw new Error(`Schedule must be locked to calculate: ${dayId}`);
        }

        // first step -- add in any missing members from the leagueModel to the leagueResultModel
        Object.values(leagueModel.members).forEach(member => {
            if (!leagueResultModel!.members[member.encodedEmail]) {
                //console.log(`adding member ${member.encodedEmail} to leagueResultModel`);
                leagueResultModel!.members[member.encodedEmail] = {
                    uid: member.uid,
                    holeStats: [],
                    holesCompleted: 0,
                    encodedEmail: member.encodedEmail,
                    teeName: leagueModel.flights.find(f => f.id == member.flightId)?.teeName || '',
                    flightId: member.flightId,
                    prevFlightId: member.flightId || null,
                    prevHandicap: member.handicap || null,
                    handicap: member.handicap || 0,
                    handicapCalcSteps: [],
                    grossScore: 0,
                    grossScoreAdjusted: 0,
                    grossScoreRanked: 0,
                    grossRank: 0,
                    grossRankText: '',
                    netScore: 0,
                    netScoreRanked: 0,
                    netRank: 0,
                    netRankText: '',
                    payouts: [],
                    roundId: '',
                    totalLeaguePayout: 0,
                    totalGrossSkinsPayout: 0,
                    totalNetSkinsPayout: 0,
                    totalPayout: 0,
                }
            }
        });

        // second step -- make sure data is good. fill in or wipe out properties as needed.
        Object.entries(leagueResultModel!.members).forEach(([encodedEmail, m]) => {
            m.encodedEmail = m.encodedEmail || encodedEmail;

            m.handicap = m.handicap || leagueModel.members[encodedEmail].handicap || 0;
            m.flightId = m.flightId || leagueModel.members[encodedEmail].flightId || '';
            m.teeName = m.teeName || leagueModel.flights.find(f => f.id == m.flightId)?.teeName || '';

            m.holeStats = m.holeStats || [];

            m.grossScore = m.grossScore || 0;
            m.grossRank = m.grossRank || 0;
            m.grossRankText = m.grossRankText || '';
            m.grossScoreRanked = m.grossScoreRanked || m.grossScore;
            m.grossScoreAdjusted = m.grossScoreAdjusted || 0;

            m.netScore = m.netScore || 0;
            m.netRank = m.netRank || 0;
            m.netRankText = m.netRankText || '';
            m.netScoreRanked = m.netScoreRanked || 0;

            // reset all payout totals and wipe out individual payouts except those
            // that we cannot calculate (custom and pin prize payouts)
            // the per-flight calculation functions below will fill this in correctly.
            //m.payouts = (m.payouts || []).filter(p => p.code === 'CUSTOM' || p.code === 'CTPTEESHT' || p.code === 'CTP2NDSHT' || p.code === 'CTP3RDSHT' || p.code === 'CTP3RDSHTOFFGRN' || p.code === 'LNGPUT');
            m.payouts = (m.payouts || []).filter(p => p.code === 'CUSTOM');
            m.totalLeaguePayout = 0;
            m.totalGrossSkinsPayout = 0;
            m.totalNetSkinsPayout = 0;
            m.totalPayout = 0;

            m.holesCompleted = m.holesCompleted || 0;

            // if it's not already 9 holes, let's recalculate in case they completed some holes but didn't complete the round.
            if (m.holesCompleted !== 9) {
                if (m.holeStats.length === 9 && m.holeStats.every(h => (h.grossScore || 0) > 0)) {
                    // all other things should be correct, right?
                    m.holesCompleted = 9;

                    // const holesToPlay = (m.holeStats.length > 0 ? (m.holeStats[0].hole == 1 ? 'front9' : 'back9') : 'all18');
                    // let totalGrossScore = 0;
                    // let totalGrossScoreAdjusted = 0;
                    // let totalNetScore = 0;

                    // m.holeStats.forEach(h => {
                    //     h.grossScore = h.grossScore || 0;
                    //     totalGrossScore += h.grossScore;

                    //     const holeInfo = courseModel.scorecard.holes.find(h1 => h1.number == h.hole) || null;
                    //     h.par = h.par || holeInfo!.par;

                    //     h.grossScoreAdjusted = Math.min(h.grossScore || 0, h.par + 2);
                    //     totalGrossScoreAdjusted += h.grossScoreAdjusted;

                    //     // calculate net score.
                    //     h.netScore = calcNetScore({ holesToPlay, grossScore: h.grossScore, holeInfo: holeInfo!, handicap: m.handicap });
                    //     totalNetScore += h.netScore || 0;

                    // });

                    // m.grossScore = totalGrossScore;
                    // m.grossScoreAdjusted = totalGrossScoreAdjusted;
                    // m.grossScoreRanked = m.grossScore;
                    // m.netScore = totalNetScore
                    // m.netScoreRanked = totalNetScore;

                }
            }

        });


        leagueModel.flights.forEach(flight => {
            // Get all members whose flightId matches this flight's id
            const membersInFlight = Object.values(leagueResultModel!.members).filter(
                member => member.flightId === flight.id
            );
            // Now membersInFlight is an array of member objects for this flight
            // You can do whatever you want with membersInFlight here
            this.calculateStrokePayouts(membersInFlight, flight);

            this.calculateRanks(membersInFlight);

            membersInFlight.forEach((member, _) => {
                // note this does not include pin prize payouts yet. See below for those (and note that pin prizes are not by flight)
                // also note we exclude skins payouts since they are not by flight and not part of the league payout
                // and should not be there yet unless we're re-calculating things for a given day.
                member.totalLeaguePayout = member.payouts.filter(p => p.code !== 'GRSSKN' && p.code !== 'NETSKN').reduce((acc, p) => acc + (p.amount || 0), 0);
                member.totalGrossSkinsPayout = 0; // Will be calculated below if needed since skins aren't by flight
                member.totalNetSkinsPayout = 0; // Will be calculated below if needed since skins aren't by flight
            })

        });

        // ok, now if this is the end of a skins session (skinsDayId1 is specified),
        // we also need to calculate skins. This involves spinning through the current week and the previous week's leagueResults
        // documents to see which holes had exactly 1 grossScore and 1 netScore that was the best of those who are participating in the gross or net skins.
        if (scheduleModel.skinsDayId1 && scheduleModel.skinsDayId1 !== scheduleModel.dayId) {
            // this also sets the totals for gross and net skins payouts for each member.
            await this.calculateSkins(leagueModel, courseModel, leagueResultModel!);
        }

        // now, add in the pin prize payouts for each member
        Object.values(leagueResultModel!.pinPrizes).forEach((pinPrize, _) => {
            const member = leagueResultModel!.members[pinPrize.encodedEmail!];
            if (member) {
                member.payouts.push({
                    id: pinPrize.id,
                    hole: pinPrize.hole,
                    teeName: pinPrize.teeName,
                    code: pinPrize.code as 'GRSACE' | 'GRSALB' | 'GRSEGL' | 'GRSBRD' | 'NETBRD' | 'GRSSKN' | 'NETSKN' | 'CTPTEESHT' | 'CTP2NDSHT' | 'CTP3RDSHT' | 'CTP3RDSHTOFFGRN' | 'LNGPUT' | 'CUSTOM' | null,
                    amount: pinPrize.amount, // This will be set by the pin prize amount
                    description: pinPrize.description,
                });
                member.totalLeaguePayout = (member.totalLeaguePayout || 0) + (pinPrize.amount || 0);
            }
        });

        // now, let's calculate the total payout for each member in the league (convenience for the UI and file exports)
        Object.values(leagueResultModel!.members).forEach((member, _) => {
            member.totalPayout = member.payouts.reduce((acc, p) => acc + (p.amount || 0), 0);
        });


        const leagueResultService = resolveType(LeagueResultService);
        await leagueResultService.update(leagueResultModel!.id, leagueResultModel!);
    }


    async removeCustomPayout(leagueId: string, dayId: string, encodedEmail: string, payoutId: string): Promise<void> {
        const resultId = `${leagueId}_${dayId}`;
        const leagueResultModel = await this.loadResults(leagueId, dayId);

        const member = leagueResultModel!.members[encodedEmail];
        if (!member) {
            throw new Error(`Member not found: ${encodedEmail}`);
        }

        member.payouts = (member.payouts || []).filter(p => p.id !== payoutId);
        member.totalPayout = (member.payouts || []).reduce((acc, p) => acc + (p.amount || 0), 0);

        const leagueResultService = resolveType(LeagueResultService);
        await leagueResultService.update(resultId, leagueResultModel!);
    }

    async addCustomPayout(leagueId: string, dayId: string, encodedEmail: string, payoutAmount: number, payoutDescription: string): Promise<void> {

        const resultId = `${leagueId}_${dayId}`;
        const leagueModel = await this.loadLeague(leagueId);
        const leagueResultModel = await this.loadResults(leagueId, dayId);

        let member = leagueResultModel!.members[encodedEmail];
        if (!member) {
            const leagueMembershipService = resolveType(LeagueMembershipService);
            const membershipModel = await leagueMembershipService.get(encodedEmail);
            member = {
                uid: membershipModel?.uid || '',
                encodedEmail: encodedEmail,
                holeStats: [],
                holesCompleted: 0,
                teeName: '',
                flightId: leagueModel.members[encodedEmail].flightId,
                prevFlightId: null,
                prevHandicap: null,
                handicap: 0,
                handicapCalcSteps: [],
                grossScore: 0,
                grossScoreAdjusted: 0,
                grossScoreRanked: 0,
                grossRank: 0,
                grossRankText: '',
                netScore: 0,
                netScoreRanked: 0,
                netRank: 0,
                netRankText: '',
                payouts: [],
                roundId: '',
                totalLeaguePayout: 0,
                totalGrossSkinsPayout: 0,
                totalNetSkinsPayout: 0,
                totalPayout: 0,
            };
            leagueResultModel!.members[encodedEmail] = member;
            //throw new Error(`Member not found: ${encodedEmail}`);
        }

        member.payouts.push({
            id: crypto.randomUUID(),
            hole: null,
            teeName: null,
            code: 'CUSTOM',
            amount: payoutAmount,
            description: payoutDescription,
        });

        member.totalPayout = (member.payouts || []).reduce((acc, p) => acc + (p.amount || 0), 0);


        const leagueResultService = resolveType(LeagueResultService);
        await leagueResultService.update(resultId, leagueResultModel!);

    }

    async assignPinPrize(leagueId: string, dayId: string, prizeId: string, code: string, encodedEmail: string, teeName: string, hole: number, amount: number, description: string): Promise<void> {

        const resultId = `${leagueId}_${dayId}`;
        const leagueModel = await this.loadLeague(leagueId);
        const leagueResultModel = await this.loadResults(leagueId, dayId);
        const scheduleModel = this.findSchedule(leagueModel, dayId);

        if (scheduleModel.status !== 'active' && scheduleModel.status !== 'locked') {
            throw new Error(`Schedule must be active or locked to assign a pin prize: ${dayId}`);
        }

        // first, unassign the prize from any other member (should never happen, but just in case)
        await this.unassignPinPrize(leagueId, dayId, prizeId);

        let member = leagueResultModel!.members[encodedEmail];

        // // Check if member has completed 9 holes
        // if (member.holesCompleted < 9) {
        //     throw new Error('Member must complete a round (9 holes) to be assigned a pin prize');
        // }

        // Add the pin prize payout to the member's payouts
        member.payouts.push({
            id: prizeId,
            hole: hole,
            teeName: teeName,
            code: code as 'GRSACE' | 'GRSALB' | 'GRSEGL' | 'GRSBRD' | 'NETBRD' | 'GRSSKN' | 'NETSKN' | 'CTPTEESHT' | 'CTP2NDSHT' | 'CTP3RDSHT' | 'CTP3RDSHTOFFGRN' | 'LNGPUT' | 'CUSTOM' | null,
            amount: amount, // This will be set by the pin prize amount
            description: description,
        });

        // Update the member's total payout
        member.totalLeaguePayout = (member.payouts || []).filter(p => p.code != 'GRSSKN' && p.code != 'NETSKN').reduce((acc, p) => acc + (p.amount || 0), 0);
        member.totalPayout = (member.payouts || []).reduce((acc, p) => acc + (p.amount || 0), 0);

        // now, update the leagueResultModel-level pin prizes array
        const existingPrize = (leagueResultModel!.pinPrizes || []).find(p => p.id === prizeId);
        if (existingPrize) {
            existingPrize.encodedEmail = encodedEmail;
            existingPrize.teeName = teeName;
            existingPrize.hole = hole;
            existingPrize.code = code;
            existingPrize.amount = amount;
            existingPrize.description = description;
        } else {
            leagueResultModel!.pinPrizes.push({
                id: prizeId,
                encodedEmail: encodedEmail,
                teeName: teeName,
                hole: hole,
                code: code,
                amount: amount,
                description: description,
            });
        }

        const leagueResultService = resolveType(LeagueResultService);
        await leagueResultService.update(resultId, leagueResultModel!);
    }

    async unassignPinPrize(leagueId: string, dayId: string, prizeId: string): Promise<void> {
        const resultId = `${leagueId}_${dayId}`;
        const leagueModel = await this.loadLeague(leagueId);
        const leagueResultModel = await this.loadResults(leagueId, dayId);
        const scheduleModel = this.findSchedule(leagueModel, dayId);

        if (scheduleModel.status !== 'active' && scheduleModel.status !== 'locked') {
            throw new Error(`Schedule must be active or locked to unassign a pin prize: ${dayId}`);
        }


        // Find the member who has this prize
        let foundMember: LeagueMemberResultModel | null = null;

        for (const [_, member] of Object.entries(leagueResultModel!.members)) {
            const payout = (member.payouts || []).find(p => p.id === prizeId);
            if (payout) {
                foundMember = member;
                break;
            }
        }


        // now, update the league-level pin prizes array
        let needsSaved = false;
        const existingPrize = (leagueResultModel!.pinPrizes || []).find(p => p.id === prizeId);
        if (existingPrize) {
            // remove the prize from the league-level pin prizes array
            leagueResultModel!.pinPrizes = leagueResultModel!.pinPrizes.filter(p => p.id !== prizeId);
            needsSaved = true;
        }

        if (foundMember) {
            // Remove the payout
            foundMember.payouts = foundMember.payouts.filter(p => p.id !== prizeId);

            // update the total league payout
            foundMember.totalLeaguePayout = foundMember.payouts.filter(p => p.code != 'GRSSKN' && p.code != 'NETSKN').reduce((acc, p) => acc + (p.amount || 0), 0);

            // Update the total payout
            foundMember.totalPayout = foundMember.payouts.reduce((acc, p) => acc + (p.amount || 0), 0);

            needsSaved = true;
        }

        if (needsSaved) {
            const leagueResultService = resolveType(LeagueResultService);
            await leagueResultService.update(resultId, leagueResultModel!);
        }
    }


    /** Activates a day, setting the schedule status to active and updating the leagueModel. Also creates a new leagueResultModel if one doesn't exist. */
    async activateDay(leagueId: string, dayId: string): Promise<LeagueResultModel | null> {

        const leagueModel = await this.loadLeague(leagueId);
        let leagueResultModel = await this.loadResults(leagueId, dayId, false);
        if (!leagueResultModel) {
            await this.createDay(leagueId, dayId);
            leagueResultModel = await this.loadResults(leagueId, dayId, false);
        }

        const scheduleModel = this.findSchedule(leagueModel, dayId);

        if (scheduleModel.status != 'notstarted') {
            throw new Error(`Schedule must be notstarted to activate: ${dayId}`);
        }

        const courseModel = await this.loadCourse(scheduleModel.courseId || leagueModel.courseId);

        if (!scheduleModel.skinsDayId1) {
            // if there is no skinsDayId1, it means this is the first week of a skins session.
            // so, we need to calculate handicaps for the first week.
            await this.calculateHandicaps(leagueModel, courseModel, leagueResultModel!);
        } else {
            // otherwise, this is the second week of a skins session.
            // we just need to propagate the current handicap and flightId to the next day.
            Object.values(leagueResultModel!.members).forEach(resultMember => {
                const leagueMember = leagueModel.members[resultMember.encodedEmail];
                resultMember.handicap = leagueMember?.handicap || 0;
                resultMember.flightId = leagueMember?.flightId || '';
                resultMember.handicapCalcSteps = ['Propagated from previous week due to being in the middle of a skins session']
            });
            console.log(`propagated handicaps and flightIds for leagueResultModel ${leagueResultModel!.id}, updating...`);
            const leagueResultService = resolveType(LeagueResultService);
            await leagueResultService.update(leagueResultModel!.id, leagueResultModel!);
        }


        scheduleModel.status = 'active';
        scheduleModel.activeAt = Timestamp.now();
        scheduleModel.updatedAt = Timestamp.now();
        const leagueService = resolveType(LeagueService);
        await leagueService.update(leagueId, leagueModel);

        return leagueResultModel;
    }

    /** Locks a day, setting the schedule status to locked and updating the leagueModel. Also copies round data from userDashboards to leagueResultModel. */
    async lockDay(leagueId: string, dayId: string, onProgress?: (current: number, total: number, message: string) => void): Promise<void> {

        const resultId = `${leagueId}_${dayId}`;
        const leagueModel = await this.loadLeague(leagueId);
        const leagueResultModel = await this.loadResults(leagueId, dayId);
        const scheduleModel = this.findSchedule(leagueModel, dayId);
        const courseModel = await this.loadCourse(scheduleModel.courseId || leagueModel.courseId);

        if (scheduleModel.status !== 'active') {
            throw new Error(`Schedule must be active to lock: ${dayId}`);
        }

        // Scan all userDashboards who have leagueRounds containing the leagueId and dayId
        // and copy their relevant data into leagueResultModel.members[encodedEmail] entry
        const userDashboardService = resolveType(UserDashboardService);
        console.log('copying round data to leagueResultModel', dayId);
        const userDashboardMemberData = await userDashboardService.copyRoundDataToLeagueResult(leagueId, dayId, courseModel, leagueModel, scheduleModel, leagueResultModel!, onProgress);

        onProgress?.(-1, -1, 'Validating...');

        // Merge the user dashboard data into the league result model
        Object.entries(userDashboardMemberData).forEach(([encodedEmail, memberDashboardData]) => {
            // If member already exists in league result, merge the data
            if (leagueResultModel!.members[encodedEmail]) {
                const existingMember = leagueResultModel!.members[encodedEmail];

                // Update with user dashboard data, preserving existing calculated fields
                existingMember.holeStats = memberDashboardData.holeStats;
                existingMember.holesCompleted = memberDashboardData.holesCompleted;
                existingMember.teeName = memberDashboardData.teeName || existingMember.teeName;
                existingMember.flightId = memberDashboardData.flightId || existingMember.flightId;
                existingMember.handicap = memberDashboardData.handicap || existingMember.handicap;
                existingMember.roundId = memberDashboardData.roundId || existingMember.roundId;

                // Update scores if we have new data
                if (memberDashboardData.grossScore > 0) {
                    existingMember.grossScore = memberDashboardData.grossScore;
                    existingMember.grossScoreAdjusted = memberDashboardData.grossScoreAdjusted;
                    existingMember.grossScoreRanked = memberDashboardData.grossScore;
                }
                if (memberDashboardData.netScore > 0) {
                    existingMember.netScore = memberDashboardData.netScore;
                    existingMember.netScoreRanked = memberDashboardData.netScore;
                }
            } else {
                // Add new member from user dashboard (should never happen?)
                leagueResultModel!.members[encodedEmail] = memberDashboardData;
            }
        });

        scheduleModel.status = 'locked';
        scheduleModel.lockedAt = Timestamp.now();
        scheduleModel.updatedAt = Timestamp.now();

        console.log('updating leagueModel', dayId);

        const leagueService = resolveType(LeagueService);
        await leagueService.update(leagueId, leagueModel);

        console.log('updating leagueResultModel', dayId);
        const leagueResultService = resolveType(LeagueResultService);
        await leagueResultService.update(resultId, leagueResultModel!);

        // now, we need to calculate the day
        onProgress?.(-1, -1, 'Calculating...');
        await this.calculateDay(leagueId, dayId);

        onProgress?.(-1, -1, 'Done!');

    }

    async validatePinPrizes(leagueId: string, dayId: string): Promise<void> {
        // validate pin prizes are awarded to eligible members (those that actually played)
        const membershipService = resolveType(LeagueMembershipService);
        const leagueResultModel = await this.loadResults(leagueId, dayId);
        const badPinPrizes: string[] = [];
        for (const pinPrize of leagueResultModel!.pinPrizes) {
            const member = leagueResultModel!.members[pinPrize.encodedEmail!];
            if (member.holesCompleted < 9) {
                const membershipModel = await membershipService.get(pinPrize.encodedEmail!);
                badPinPrizes.push(`${membershipModel?.firstName || member.encodedEmail || '--not found--'} ${membershipModel?.lastName}`);
            }
        }
        if (badPinPrizes.length > 0) {
            throw new Error(`The following members did not complete 9 holes to be eligible for pin prizes: ${badPinPrizes.join(', ')}`);
        }
    }

    /** Completes a day, setting the schedule status to completed and updating the leagueModel. Also calculates handicaps and saves the leagueResultModel. */
    async completeDay(leagueId: string, dayId: string): Promise<void> {

        await this.validatePinPrizes(leagueId, dayId);

        // const resultId = `${leagueId}_${dayId}`;
        const leagueModel = await this.loadLeague(leagueId);
        // const courseModel = await this.loadCourse(leagueModel.courseId);
        // const leagueResultModel = await this.loadResults(leagueId, dayId);
        const scheduleModel = this.findSchedule(leagueModel, dayId);
        if (scheduleModel.status != 'locked') {
            throw new Error(`Schedule must be locked to complete: ${dayId}`);
        }

        // now let's save the leagueModel
        scheduleModel.status = 'completed';
        scheduleModel.updatedAt = Timestamp.now();
        scheduleModel.completedAt = Timestamp.now();
        const leagueService = resolveType(LeagueService);
        await leagueService.update(leagueId, leagueModel);


    }

    /** Cancels a day, setting the schedule status to cancelled and updating the leagueModel. */
    async cancelDay(leagueId: string, dayId: string): Promise<void> {
        const leagueModel = await this.loadLeague(leagueId);
        const scheduleModel = this.findSchedule(leagueModel, dayId);

        if (scheduleModel.status === 'completed') {
            throw new Error(`Cannot cancel a completed day: ${dayId}`);
        }

        // Set the schedule status to cancelled
        scheduleModel.status = 'cancelled';
        scheduleModel.updatedAt = Timestamp.now();

        const leagueService = resolveType(LeagueService);
        await leagueService.update(leagueId, leagueModel);
    }

}